import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Mail, Lock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const returnTo = loc.state?.from || "/";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setUser(data.user);
      toast.success(`${t("nav.signIn")} — ${data.user.name || data.user.email}`);
      nav(returnTo, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md items-center px-6 py-10">
      <div className="w-full rounded-3xl bg-white p-8 card-shadow">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="EasyStay" className="h-9 w-9 rounded-xl object-cover" />
          <span className="font-display text-xl font-bold text-slate-900">Easy<span className="text-copper">Stay</span></span>
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold text-slate-900">{t("auth.login.title")}</h1>
        <p className="mt-1 text-sm text-slate-600">{t("auth.login.subtitle")}</p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">{t("auth.login.email")}</label>
            <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 focus-within:border-copper focus-within:bg-white">
              <Mail className="h-4 w-4 text-slate-400" />
              <input
                data-testid="login-email"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-transparent py-3 text-sm outline-none"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">{t("auth.login.password")}</label>
            <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 focus-within:border-copper focus-within:bg-white">
              <Lock className="h-4 w-4 text-slate-400" />
              <input
                data-testid="login-password"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-transparent py-3 text-sm outline-none"
              />
            </div>
          </div>
          <button data-testid="login-submit" type="submit" disabled={loading} className="btn-primary w-full text-sm">
            {loading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : t("auth.login.submit")}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
          <span className="h-px flex-1 bg-slate-200" /> {t("auth.login.orGoogle")} <span className="h-px flex-1 bg-slate-200" />
        </div>

        <button data-testid="login-google" onClick={googleLogin} className="btn-outline w-full text-sm">
          {t("auth.login.orGoogle")}
        </button>

        <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
          <Link to="/forgot-password" data-testid="link-forgot" className="hover:text-copper">{t("auth.login.forgot")}</Link>
          <Link to="/register" data-testid="link-register" className="font-semibold text-copper hover:underline">{t("auth.login.register")} →</Link>
        </div>
      </div>
    </div>
  );
}
